import React, { useContext, useMemo, useState } from 'react'
import { AppContext } from '@state/context'
import { useCurrentUser } from '@hooks/useCurrentUser'
import Heading from '@components/Heading'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  DIVIDER_SIZE,
  HEADING_TYPE,
  PANELS,
  TEXT_ALIGN,
  TEXT_SIZES,
} from '@helpers/constants'
import Divider from '@components/Divider'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Form from '@components/Form'
import Text from '@components/Text'
import { useUserSchoolClasses } from '@hooks/useUserSchoolClasses'
import { Controller, useForm } from 'react-hook-form'
import { useMigrateMySchoolClass } from '@hooks/mutations/useMigrateMySchoolClass'
import { useDisconnectCustomerSchoolClass } from '@hooks/mutations/useDisconnectCustomerSchoolClass'
import { useNavigate } from 'react-router-dom'
import { useAddToastWithTimer } from '@hooks/useAddToastWithTimer'
import Button from '@components/Button'
import { closePanel } from '@state/actions/panelsActions'

const MigrateMySchoolClassPanel = () => {
  const { currentUser } = useCurrentUser()
  const [, dispatch] = useContext(AppContext)
  const [isLoading, setIsLoading] = useState(false)
  const {
    schoolClasses,
    isLoading: isLoadingSchoolClasses,
    error: schoolClassError,
  } = useUserSchoolClasses()
  const { handleSubmit, control, watch } = useForm()
  const schoolClassWatch = watch('schoolClass')
  const { post, isPosting } = useMigrateMySchoolClass()
  const { post: postDisconnect, isPosting: isPostingDisconnect } =
    useDisconnectCustomerSchoolClass()
  const { addToastWithTimer } = useAddToastWithTimer()
  const navigate = useNavigate()
  const mySchoolClass = useMemo(
    () => schoolClasses?.find((sc) => sc.isPrivate),
    [isLoadingSchoolClasses, schoolClasses]
  )

  const schoolClassOptions = useMemo(() => {
    if (!schoolClasses) {
      return undefined
    }

    return schoolClasses
      .filter((sc) => !sc.isPrivate)
      .map((sc) => ({
        name: sc.name,
        value: sc.id,
      }))
  }, [isLoadingSchoolClasses, schoolClasses])

  const afterPost = (res) => {
    if (res.success) {
      navigate('lararrum')
      dispatch(closePanel(PANELS.MIGRATE_MY_SCHOOL_CLASS))
    } else {
      addToastWithTimer(
        'Någonting gick fel. Försök igen senare och kontakta oss om felet kvarstår.',
        true
      )
    }
  }

  const onSubmit = async (data) => {
    if (!data?.schoolClass?.value) {
      throw new Error('something just went wrong')
    }
    setIsLoading(true)
    const migrateRes = await post({ toSchoolClassId: data?.schoolClass?.value }, null, 'Någonting gick fel. Försök igen senare och kontakta oss om felet kvarstår.')
    if (!migrateRes.success) {
      addToastWithTimer(
        'Någonting gick fel. Försök igen senare och kontakta oss om felet kvarstår.',
        true
      )

      setIsLoading(false)

      return
    }

    const disconnectRes = await disconnectSchoolClass()
    if (!disconnectRes.success) {
      addToastWithTimer(
        'Någonting gick fel. Försök igen senare och kontakta oss om felet kvarstår.',
        true
      )

      setIsLoading(false)

      return
    }

    setIsLoading(false)

    navigate('lararrum')
    dispatch(closePanel(PANELS.MIGRATE_MY_SCHOOL_CLASS))
  }

  const disconnectSchoolClass = async () => {
    return await postDisconnect({
      disconnectSchoolClasses: [mySchoolClass.id],
      id: currentUser.id,
    }, null, 'Någonting gick fel. Försök igen senare och kontakta oss om felet kvarstår.')
  }

  return (
    <>
      <div className={'relative'}>
        <div className='absolute top-0 right-0'>
          <Button
            handleClick={() => {
              dispatch(closePanel(PANELS.MIGRATE_MY_SCHOOL_CLASS))
            }}
            size={BUTTON_SIZE.NO_PADDING}
            icon={'Close'}
          ></Button>
        </div>
      </div>

      <div className={'flex flex-col items-center'}>
        <Heading headingLevel={HEADING_TYPE.H1} size={TEXT_SIZES.LARGE}>
          Flytta böcker
        </Heading>
        <Divider size={DIVIDER_SIZE.SMALL} />

        <Text align={TEXT_ALIGN.LEFT} bold>
          Kom igång med Doklet premium!
        </Text>
        <Text align={TEXT_ALIGN.LEFT}>
          Flytta över böckerna i Min klass till din nya klass, så raderas Min klass.
        </Text>

        <Divider size={DIVIDER_SIZE.SMALL} />

        {schoolClassOptions && !isLoadingSchoolClasses && !schoolClassError && (
          <Form
            onSubmit={handleSubmit(onSubmit)}
            className='w-2/3 flex flex-col justify-center'
          >
            {mySchoolClass?.books?.length > 0 && (
              <>
                <Form.Label htmlFor='schoolClasses'>Välj klass</Form.Label>
                <Controller
                  control={control}
                  name={'schoolClass'}
                  defaultValue={undefined}
                  render={(
                    { onChange, onBlur, value, name, ref },
                    { invalid, isTouched, isDirty }
                  ) => (
                    <Form.MultiSelect
                      name={name}
                      onChange={(options) => onChange(options)}
                      options={schoolClassOptions}
                      placeholder={
                        schoolClassOptions.length === 0
                          ? 'Alla klasser är redan tillagda i ditt lärarrum'
                          : 'Välj klass'
                      }
                      isSearchable
                      isDisabled={schoolClassOptions.length === 0}
                    />
                  )}
                />

                <Divider size={DIVIDER_SIZE.SMALL} />
              </>
            )}

            <div className={'flex justify-center gap-2'}>
              {(mySchoolClass?.books?.length > 0 || isLoading) && (
                <ButtonWithLoader
                  type={BUTTON_TYPE.PRIMARY}
                  isLoading={isLoading}
                  enabledText={'Flytta böcker'}
                  disabled={
                    !schoolClassWatch ||
                    isLoading ||
                    schoolClassOptions.length === 0
                  }
                />
              )}

              <ButtonWithLoader
                type={BUTTON_TYPE.ALERT_RED_DARK}
                isLoading={isPostingDisconnect && !isLoading}
                disabledText={'Radera'}
                enabledText={'Radera'}
                disabled={isPostingDisconnect || isLoading}
                handleClick={async () => afterPost(await disconnectSchoolClass())}
                nativeTypeAttribute='button'
              />
            </div>
          </Form>
        )}
      </div>
    </>
  )
}

export default MigrateMySchoolClassPanel
